import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { useLocation, useHistory } from 'react-router-dom';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import Notification from '@this/src/notification';
import TripAiMessagesContainer from './components/TripAiMessagesContainer';
import { useTrip } from './hooks/useTrip';
import { useReservationInfo } from './hooks/useReservationInfo';
import {
  sendAiMessage,
  fetchProposalBundles,
  rejectProposalBundle,
  selectProposalItem,
  completeProposalSelection,
  saveReservationInfo
} from './api';
import type { ProposalBundle } from './api';
import type { ChatMessage } from './types/messages';
import type { FormValues } from './components/message_section/ReservationInfoFormTypes';
import type { UserInfo } from './types/user';
import type { ProposalNotification } from './hooks/useAiMessages';

type RouteParams = {
  id: string;
};

type LocationState = {
  initialMessage?: string;
};

interface Props extends RouteComponentProps<RouteParams> {
  user: UserInfo;
}

const TripAiMessagesPage: React.FC<Props> = ({ match, user }) => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { trip, pusherInfo, loading, error, refetch } = useTrip(match.params.id);
  const tripId = match.params.id;
  const { reservationInfo, refetch: refetchReservationInfo } = useReservationInfo(tripId);

  const [proposalNotification, setProposalNotification] = useState<ProposalNotification | null>(null);
  const [proposalBundles, setProposalBundles] = useState<ProposalBundle[]>([]);
  const [isSubmittingReservationInfo, setIsSubmittingReservationInfo] = useState(false);
  const [selectionRequests, setSelectionRequests] = useState(0);

  const handleProposalReceived = useCallback((notification: ProposalNotification) => {
    setProposalNotification(notification);
  }, []);

  const handleSelectItem = useCallback(
    async (bundleId: number, itemId: number) => {
      const targetBundle = proposalBundles.find(bundle => bundle.id === bundleId);
      if (!targetBundle) {
        return;
      }
      const targetItem = targetBundle.items.find(item => item.id === itemId);
      if (!targetItem || targetItem.selected) {
        return;
      }

      const previousBundles = proposalBundles;
      setProposalBundles(prev =>
        prev.map(bundle => {
          if (bundle.id !== bundleId) return bundle;
          return {
            ...bundle,
            state: 'selected' as const,
            items: bundle.items.map(item => ({
              ...item,
              selected: item.id === itemId
            }))
          };
        })
      );

      try {
        setSelectionRequests(prev => prev + 1);
        await selectProposalItem(tripId, bundleId, itemId);
      } catch (e) {
        // 一旦
        // eslint-disable-next-line no-console
        console.error('[TripAiMessagesPage] Failed to select item:', e);
        setProposalBundles(previousBundles);
      } finally {
        setSelectionRequests(prev => (prev > 0 ? prev - 1 : 0));
      }
    },
    [tripId, proposalBundles]
  );

  const handleCloseBundle = useCallback(
    async (bundleId: number) => {
      const targetBundle = proposalBundles.find(bundle => bundle.id === bundleId);
      if (!targetBundle || targetBundle.state === 'rejected') {
        return;
      }

      const previousBundles = proposalBundles;
      setProposalBundles(prev =>
        prev.map(bundle => {
          if (bundle.id !== bundleId) return bundle;
          return {
            ...bundle,
            state: 'rejected' as const,
            items: bundle.items.map(item => ({
              ...item,
              selected: false
            }))
          };
        })
      );

      try {
        setSelectionRequests(prev => prev + 1);
        await rejectProposalBundle(tripId, bundleId);
      } catch (e) {
        // 一旦
        // eslint-disable-next-line no-console
        console.error('[TripAiMessagesPage] Failed to reject bundle:', e);
        setProposalBundles(previousBundles);
      } finally {
        setSelectionRequests(prev => (prev > 0 ? prev - 1 : 0));
      }
    },
    [tripId, proposalBundles]
  );

  useEffect(() => {
    if (!proposalNotification) {
      return;
    }

    const loadBundles = async () => {
      try {
        const bundles = await fetchProposalBundles(tripId);
        setProposalBundles(bundles);
      } catch (e) {
        // 一旦
        // eslint-disable-next-line no-console
        console.error('[TripAiMessagesPage] Failed to fetch proposal bundles:', e);
      }
    };

    loadBundles();
  }, [proposalNotification, tripId]);

  const handleSubmitReservationInfo = useCallback(
    async (values: FormValues): Promise<boolean> => {
      setIsSubmittingReservationInfo(true);
      try {
        const result = await saveReservationInfo(tripId, {
          finalDestination: values.finalDestination,
          purpose: values.purpose,
          internalNumber: values.internalNumber,
          chargingDepartmentId: values.chargingDepartmentId,
          projectId: values.projectId,
          organizationAddressId: values.shinkansen?.organizationAddressId,
          shinkansenAddressType: values.shinkansen?.shinkansenAddressType,
          postcode: values.shinkansen?.postcode,
          shinkansenAddress: values.shinkansen?.shinkansenAddress,
          shinkansenAddressee: values.shinkansen?.shinkansenAddressee,
          traveler: values.traveler
        });
        if (result.success) {
          await refetch();
          await refetchReservationInfo();
          return true;
        }
        Notification.error(result.error || '保存に失敗しました');
        return false;
      } catch (e) {
        Notification.error('保存に失敗しました。時間をおいてもう一度お試しください。');
        return false;
      } finally {
        setIsSubmittingReservationInfo(false);
      }
    },
    [tripId, refetch, refetchReservationInfo]
  );

  useEffect(() => {
    if (proposalBundles.length === 0) {
      return;
    }
    if (selectionRequests > 0) {
      return;
    }

    const allDecided = proposalBundles.every(bundle => bundle.state === 'selected' || bundle.state === 'rejected');

    if (allDecided) {
      const complete = async () => {
        try {
          await completeProposalSelection(tripId);
        } catch (e) {
          // 一旦
          // eslint-disable-next-line no-console
          console.error('[TripAiMessagesPage] Failed to complete proposal selection:', e);
        }
      };
      complete();
    }
  }, [proposalBundles, tripId, selectionRequests]);

  const initialMessage = useMemo(() => {
    return location.state?.initialMessage;
  }, [location.state]);

  const initialMessages: ChatMessage[] = useMemo(() => {
    if (!initialMessage) return [];
    return [
      {
        id: `initial-${tripId}`,
        direction: 'user',
        text: initialMessage,
        createdAt: new Date().toISOString()
      },
      {
        id: `optimistic-${tripId}`,
        direction: 'ai',
        text: '応答を生成中です...',
        createdAt: new Date().toISOString(),
        type: 'optimistic'
      }
    ];
  }, [initialMessage, tripId]);

  const hasSentInitialRef = useRef(false);
  const prevTripIdRef = useRef(tripId);
  const prevInitialMessageRef = useRef(initialMessage);

  useEffect(() => {
    if (prevTripIdRef.current !== tripId || prevInitialMessageRef.current !== initialMessage) {
      hasSentInitialRef.current = false;
      prevTripIdRef.current = tripId;
      prevInitialMessageRef.current = initialMessage;
    }
  }, [tripId, initialMessage]);

  useEffect(() => {
    if (!initialMessage || hasSentInitialRef.current) return;
    if (!trip || !pusherInfo) return;

    hasSentInitialRef.current = true;
    const sendInitial = async () => {
      try {
        await sendAiMessage(tripId, initialMessage, []);
        history.replace(location.pathname, {});
      } catch {
        Notification.error('送信に失敗しました。時間をおいてもう一度お試しください。');
      }
    };
    sendInitial();
  }, [initialMessage, tripId, trip, pusherInfo, history, location.pathname]);

  if (error) {
    return <div style={{ padding: '24px' }}>{error}</div>;
  }

  const allowPartial = Boolean(initialMessage);
  if ((loading || !trip || !pusherInfo) && !allowPartial) {
    return <Loading />;
  }

  return (
    <TripAiMessagesContainer
      tripId={tripId}
      trip={trip || null}
      user={user}
      pusherKey={pusherInfo?.key || ''}
      pusherChannelPrefix={pusherInfo?.channelPrefix || ''}
      headerTitle={`旅程番号：${tripId}`}
      subtitle="AI予約モード"
      enableMessaging={!loading && !!trip && !!pusherInfo}
      initialMessages={initialMessages}
      onProposalReceived={handleProposalReceived}
      onReservationFormReceived={refetchReservationInfo}
      proposalBundles={proposalBundles}
      onSelectItem={handleSelectItem}
      onCloseItem={handleCloseBundle}
      reservationInfo={reservationInfo}
      onSubmitReservationInfo={handleSubmitReservationInfo}
      isSubmittingReservationInfo={isSubmittingReservationInfo}
    />
  );
};

export default TripAiMessagesPage;
